import { useContext, useEffect } from 'react';
import { useState } from 'react';
import { notification } from 'antd';
import { ApiContext } from 'utils/networking/Api.context';
import { AppContext } from 'App.context';
import SupportedCommunicationChannels from './SupportedCommunicationChannels';

export default function useCommunicationChannelsHook() {
  const { API } = useContext(ApiContext);
  const {
    appState,
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);

  const [state, setState] = useState({
    isLoading: false,
    selectedHotel: null,
    legalDocuments: null,
    replacementTags: null,
    previewText: null,
    languages: null,
    selectedLanguage: null,
    senderCheckinEmail: [],
    emailCheckin: [],
  });

  useEffect(() => {
    actions.initView();
  }, [appState.hotelList]);

  useEffect(() => {
    actions.onHotelSelected(getSelectedHotel());
  }, [appState.selectedHotel]);

  const actions = {
    initView: () => {
      if (appState?.hotelList && appState?.hotelList.length) {
        const firstHotel = {
          key: appState?.hotelList[0]?.id,
          name: appState?.hotelList[0]?.name,
        };

        updateState({ selectedHotel: firstHotel });
        actions.loadLegalDocuments(firstHotel.key);
      }
    },

    loadLegalDocuments: hotelId => {
      updateState({ isLoading: true });

      API.communicationChannelsRequest(hotelId)
        .then(data => {
          const legalDocuments = data.hotelTexts.reduce((result, document) => {
            result[document.type] = result[document.type] || [];
            result[document.type].push(document);
            return result;
          }, Object.create(null));

          const filledDocuments = actions.addMissingDocuments(legalDocuments, data?.languages);
          const orderedDocuments = actions.getOrderedTypes(filledDocuments);

          //FIX ME
          orderedDocuments.push(checkoutTemplates);

          updateState({
            legalDocuments: orderedDocuments,
            replacementTags: data.tags,
            languages: data.languages,
            isLoading: false,
            selectedLanguage: data.languages[0].code,
            senderCheckinEmail: data?.emails?.sender_checkin_email?.split(',')?.filter(i => i),
            emailCheckin: data?.emails?.email_checkin?.split(',')?.filter(i => i),
          });
        })
        .catch(error => {
          console.warn(error);
          updateState({ isLoading: false });
        });
    },

    addMissingDocuments: (legalDocuments, languages) => {
      SupportedCommunicationChannels.forEach(type => {
        const loadedDocument = legalDocuments[type.id];
        if (!loadedDocument) {
          legalDocuments[type.id] = [];
        }
      });

      languages.forEach(lang => {
        SupportedCommunicationChannels.forEach(type => {
          const alreadyExists = legalDocuments[type.id].find(
            document => document.lang == lang.code
          );

          if (!alreadyExists) {
            legalDocuments[type.id].push({
              type: type.id,
              lang: lang.code,
              value: '',
            });
          }
        });
      });

      return legalDocuments;
    },

    getOrderedTypes: legalDocuments => {
      const orderedDocuments = [];
      orderedDocuments.push(legalDocuments['checkin_template']);

      return orderedDocuments;
    },

    updateLegalText: (type, value, label, applyToAllHotels) => {
      updateState({ isLoading: true });
      API.updateLegalTextRequest({
        hotelId: !applyToAllHotels ? state.selectedHotel?.key : null,
        type,
        value,
        lang: state.selectedLanguage,
        label: label,
      })
        .then(_ => {
          notification.success({
            message: translate('legalDocuments.success'),
          });
          if (state.selectedHotel) {
            const legalDocumentsCopy = Object.assign([], state.legalDocuments);
            actions.updateDocumentsLocally(legalDocumentsCopy, type, value, label);

            updateState({ isLoading: false, legalDocuments: legalDocumentsCopy });
          } else {
            updateState({ isLoading: false });
          }
        })
        .catch(err => {
          notification.error({
            message: translate('legalDocuments.error'),
          });
          updateState({ isLoading: false });
        });
    },

    updateDocumentsLocally: (legalDocumentsCopy, type, value, label) => {
      const modifiedSet = legalDocumentsCopy.find(set => set[0].type == type);
      actions.updateLocalSet({ documentsSet: modifiedSet, type, value });

      const modifiedLabelSet = legalDocumentsCopy.find(set => set[0].type == `${type}-label`);
      if (modifiedLabelSet) {
        actions.updateLocalSet({
          documentsSet: modifiedLabelSet,
          type: `${type}-label`,
          value: label,
        });
      }
    },

    updateLocalSet: ({ documentsSet, type, value }) => {
      const modifiedLabelDocument = documentsSet?.find(
        document => document.lang == state.selectedLanguage
      );

      if (modifiedLabelDocument) {
        modifiedLabelDocument.value = value;
      } else {
        documentsSet.push({
          type,
          lang: state.selectedLanguage,
          value: value,
        });
      }
    },

    onHotelSelected: hotel => {
      updateState({ selectedHotel: hotel });
      if (hotel) {
        actions.loadLegalDocuments(hotel?.key);
      }
    },

    onPreviewPressed: htmlText => {
      if (state.replacementTags) {
        Object.keys(state.replacementTags).forEach(key => {
          htmlText = htmlText.replaceAll(`[${key}]`, state.replacementTags[key]);
        });
      }

      updateState({ previewText: htmlText });
    },

    onSavePressed: (type, value, label, applyToAllHotels) => {
      actions.updateLegalText(type, value, label, applyToAllHotels);
    },

    onLanguageSelected: lang => {
      updateState({ selectedLanguage: lang });
    },

    setSenderCheckinEmail: tags => {
      updateState({ senderCheckinEmail: tags });
    },

    setEmailCheckin: tags => {
      updateState({ emailCheckin: tags });
    },

    onSaveEmailsPressed: () => {
      updateState({ isLoading: true });

      API.updateCommunicationEmails({
        hotelId: state.selectedHotel?.key,
        senderCheckinEmail: state.senderCheckinEmail.join(),
        emailCheckin: state.emailCheckin.join(),
      })
        .then(_ => {
          updateState({ isLoading: false });
          notification.success({
            message: translate('communicationChannels.emailsSuccess'),
          });
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
        });
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}

const checkoutTemplates = [
  {
    lang: 'en',
    type: 'checkout_template',
    value: `<p>Thank you for your stay and we hope to see you again very soon.</p>
    <p><img src="[hotel_logo]" width="auto" height="100"> </p>
    <p> [hotel_name] </p>`,
  },
  {
    lang: 'es',
    type: 'checkout_template',
    value: `<p>Gracias por su estancia y esperamos volver a verle muy pronto.</p>
    <p><img src="[hotel_logo]" width="auto" height="100"> </p>
    <p> [hotel_name] </p>`,
  },
];
